import { useMemo } from 'react'
import { ApolloClient, from, NormalizedCacheObject } from '@apollo/client'
import headersLink from '~/lib/apolloClient/headersLink'
import batchHttpLink from '~/lib/apolloClient/batchHttpLink'
import unauthorizedLink from '~/lib/apolloClient/unauthorizedLink'
import errorHandlerLink from '~/lib/apolloClient/errorHandlerLink'
import createApolloCache from '~/lib/apolloClient/cache'
import merge from 'deepmerge'
import isEqual from 'lodash/isEqual'
import { GetServerSidePropsContext } from 'next'
import { Translate } from '~/types/Translation'

let apolloClient: ApolloClient<NormalizedCacheObject>

const isBrowser = typeof window !== 'undefined'
const dev = process.env.NODE_ENV !== 'production'

export type Notify = (renderCallback: () => any) => void

function createApolloClient(context?: GetServerSidePropsContext, notify?: Notify, t?: Translate) {
  const additiveLink = from([
    headersLink(context),
    errorHandlerLink(notify, t),
    unauthorizedLink,
    batchHttpLink(context),
  ])

  return new ApolloClient({
    connectToDevTools: dev && isBrowser,
    ssrMode: !isBrowser,
    link: additiveLink,
    cache: createApolloCache(),
  })
}

export function initializeApollo(
  context?: GetServerSidePropsContext,
  initialState?: NormalizedCacheObject,
  notify?: Notify,
  t?: Translate
) {
  const _apolloClient = apolloClient ?? createApolloClient(context, notify, t)

  if (initialState) {
    const existingCache = _apolloClient.extract()
    const data = merge(existingCache, initialState, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter(d => sourceArray.every(s => !isEqual(d, s))),
      ],
    })

    _apolloClient.cache.restore(data)
  }

  // Always return new apolloClient on the server, otherwise all users would have the same client.
  if (!isBrowser) return _apolloClient

  if (!apolloClient) apolloClient = _apolloClient
  return _apolloClient
}

export function useApollo(initialState: NormalizedCacheObject, notify: Notify, t: Translate) {
  return useMemo(
    () => initializeApollo(undefined, initialState, notify, t),
    [initialState, notify, t]
  )
}
